/**
 * Plans and runs one data tile through the query queue, or each panel of a
 * repeated tile. Shared by the tile runner (stale tiles of the active page)
 * and the export (every tile of the chosen pages that is not ready).
 */

import type { StoreApi } from "zustand"
import { buildTableModel, messageTableModel } from "utils/tableModel"
import type { TableModel } from "utils/tableModel"
import type {
  EngineOutputItem,
  EngineQueryResult,
  RTableContent,
} from "types/engineOutput.types"
import { isDataTile } from "features/Dashboard/types/dashboardDocument"
import { TRACE_CHECK } from "features/Provenance/utils/traceTable"
import { DashboardState, findTile } from "./dashboardStore"
import { QueryQueue, SupersededRunError } from "./queryQueue"
import { PanelPlan, planTileRun } from "./planTileRun"
import { tileTableId } from "./tileScript"
import { repeatOf, variableLabel } from "./panels"
import type { UniverseSpec } from "./universeComposer"

const errorText = (e: unknown): string =>
  e instanceof Error ? e.message : String(e)

const EXISTING_TABLE = /Result table with (\S+) already exists/
const NO_OUTPUT = "The script produced no output."

/**
 * A name the dashboard owns in the session: a `__dq_` temporary or
 * universe, the tile's result table, or one of its panels' (`t_<tile>_<k>`).
 */
const ownsName = (name: string, tileId: string): boolean => {
  const table = tileTableId(tileId)
  return (
    name.startsWith("__dq_") ||
    name === table ||
    (name.startsWith(`${table}_`) && /^\d+$/.test(name.slice(table.length + 1)))
  )
}

/**
 * The names a failed run of `script` can leave behind in the session, last
 * declared first: its result tables and the root of each QTABLE / FFIELD it
 * declares. The engine stops at the first failing line without undoing the
 * lines before it, and it refuses to declare a name twice, so a leftover
 * would fail every later run of the same script. Only names the dashboard
 * owns are listed (`__dq_` temporaries and universes, and the tile's own
 * result table or its panels'): a script tile's own declarations may shadow
 * the prep script's, so they are never removed. A panel's script declares
 * only that panel's names, so its clean-up leaves the other panels alone.
 */
export const leftoverNames = (script: string, tileId: string): string[] => {
  const names: string[] = []
  script.split("\n").forEach((line) => {
    const m = /^\s*(?:QTABLE|FFIELD|XTAB|DTAB)\s+(\S+)/.exec(line)
    const name = m?.[1].split("::")[0]
    if (!name || names.includes(name)) return
    if (ownsName(name, tileId)) names.push(name)
  })
  return names.reverse()
}

/** Queues a REMOVE of each name; a name that is not there is not an error. */
const removeNames = (queue: QueryQueue, names: string[]): Promise<unknown> =>
  Promise.all(
    names.map((n) => queue.runPlain(`REMOVE ${n}`).catch(() => undefined))
  )

/** A line that declares a filter universe (`composeUniverse`). */
const UNIVERSE_DECLARATION = /^\s*(?:QTABLE|APPEND|FFIELD)\s+(?:<<)?__dq_[cf]_/

/**
 * `script` without the declaration of a filter universe: once a tile has
 * declared it, the session keeps it, and a second declaration is refused.
 */
export const withoutUniverseDeclaration = (script: string): string =>
  script
    .split("\n")
    .filter((line) => !UNIVERSE_DECLARATION.test(line))
    .join("\n")

/**
 * Runs the check a trace of a cell of tile `tileId` sends (the tile's script
 * with a check table added after its table's line, see the trace panel) in
 * turn with the tiles' runs, and gives its output. The universe the tile ran
 * in is still declared, so the script does not declare it again. After a
 * failure the check table and what the script declares that the dashboard
 * owns are removed, as after a failed run of the tile.
 */
export const runTraceCheck = async (
  queue: QueryQueue,
  tileId: string,
  script: string
): Promise<EngineOutputItem[]> => {
  const own = withoutUniverseDeclaration(script)
  try {
    const result = await queue.runPlain(own)
    return result.result?.output ?? []
  } catch (e) {
    if (!(e instanceof SupersededRunError))
      await removeNames(queue, [TRACE_CHECK, ...leftoverNames(own, tileId)])
    throw e
  }
}

/**
 * Runs `script`. When the engine refuses because a result table from an
 * earlier failed run is still there, and the dashboard owns that table,
 * removes it and this run's own leftovers, then runs the script once more.
 * A table the dashboard does not own is a plain failure. `isCurrent` is
 * false once the session has been reset, when there is nothing to clean up.
 */
const runWithRetry = async (
  queue: QueryQueue,
  script: string,
  tileId: string,
  isCurrent: () => boolean
): Promise<EngineQueryResult> => {
  try {
    return await queue.enqueue(script)
  } catch (e) {
    const table = EXISTING_TABLE.exec(errorText(e))?.[1]
    if (!table || !ownsName(table, tileId) || !isCurrent()) throw e
    const names = [
      table,
      ...leftoverNames(script, tileId).filter((n) => n !== table),
    ]
    // Queued back to back so no other script runs between the clean-up and
    // the retry.
    void removeNames(queue, names)
    return queue.enqueue(script)
  }
}

/**
 * The model to draw for an output item. An output that is not a table comes
 * back as a message the tile shows as text (with a one-cell model holding
 * the same text).
 */
const toTileResult = (
  item: EngineOutputItem
): { model: TableModel; message?: string } => {
  const content = item.content as RTableContent
  const isTable =
    item.type === "rtable" ||
    Array.isArray((item.content as { data?: unknown } | undefined)?.data)
  if (isTable) return { model: buildTableModel(content, item.label) }
  const message = item.message ?? "The output is not a table."
  return { model: messageTableModel(message), message }
}

/**
 * After a failed run in the current session: clears what the run left
 * behind, so the next run of this tile, and the next declaration of its
 * universe, start clean, and forgets a universe it declared, so the next
 * tile that needs it declares it again. The weight needs nothing: every
 * script starts with its own weight line (see `buildTileScript`). After a
 * RESET the engine holds none of the run's leftovers, the same names may
 * already belong to the new session, and the known universes are cleared
 * anyway: nothing to do.
 */
const cleanUpFailedRun = (
  store: StoreApi<DashboardState>,
  queue: QueryQueue,
  tileId: string,
  script: string,
  declared: UniverseSpec | null,
  isCurrent: () => boolean
): void => {
  if (!isCurrent()) return
  void removeNames(queue, leftoverNames(script, tileId))
  if (declared) {
    const now = store.getState()
    const known = new Set(now.session.knownFilterFields)
    known.delete(declared.fieldName)
    now.setSession({ knownFilterFields: known })
  }
}

/**
 * Runs every panel of a repeated tile through the queue, each landing in
 * the store on its own (the store drops a panel result for a script the
 * tile has moved on from), and resolves once every panel has settled. A
 * panel that declared its universe also caches its result for its script
 * without the declaration, so a Retry, which plans the tile again, sends
 * only the panels that failed.
 */
const runPanels = async (
  store: StoreApi<DashboardState>,
  queue: QueryQueue,
  tileId: string,
  panels: PanelPlan[],
  outputIndex: number,
  isCurrent: () => boolean
): Promise<void> => {
  const state = store.getState()
  state.setPanelsQueued(
    tileId,
    panels,
    panels.some((p) => p.usesUniverse)
  )
  state.setTileRunning(tileId)
  const { applyPanelResult, setPanelError, markKnownFilterField } = state
  await Promise.allSettled(
    panels.map(async ({ category, script, universe, emitPrefix, quietScript }) => {
      const declared = universe && emitPrefix ? universe : null
      if (declared) markKnownFilterField(declared.fieldName)
      try {
        const result = await runWithRetry(queue, script, tileId, isCurrent)
        if (quietScript) queue.seed(quietScript, result)
        const output = result.result?.output
        const item = output?.[outputIndex] ?? output?.[0]
        if (!item) {
          setPanelError(tileId, category, script, NO_OUTPUT)
          return
        }
        const { model, message } = toTileResult(item)
        // A panel draws a chart: an output that is not a table is its message.
        if (message) setPanelError(tileId, category, script, message)
        else applyPanelResult(tileId, category, script, model)
      } catch (e) {
        if (e instanceof SupersededRunError) return
        cleanUpFailedRun(store, queue, tileId, script, declared, isCurrent)
        setPanelError(tileId, category, script, errorText(e))
      }
    })
  )
}

/**
 * Plans and runs one data tile, and resolves once its result or error is in
 * the store (a result for a script the tile has moved on from is dropped by
 * the store). A repeated tile runs each of its panels.
 */
export const runTile = async (
  store: StoreApi<DashboardState>,
  queue: QueryQueue,
  tileId: string
): Promise<void> => {
  const state = store.getState()
  // While the preparation has failed no tile runs (the export asks too).
  if (state.session.status === "prep-error") return
  const found = findTile(state, tileId)
  if (!found || !isDataTile(found.tile)) return
  const tile = found.tile
  const plan = planTileRun(state, tileId)
  if (!plan) return
  const {
    setTileQueued,
    setTileRunning,
    applyTileResult,
    setTileError,
    markKnownFilterField,
  } = state

  const empty =
    tile.source.kind === "builder"
      ? tile.source.rows.length === 0 && "Drop a variable here"
      : !tile.source.script.trim() && "Write a script for this tile"
  // Nothing to run yet: every tile type shows the prompt as text.
  if (empty) {
    setTileQueued(tileId, "", true)
    applyTileResult(
      tileId,
      "",
      { label: tileId, content: { data: [] } },
      messageTableModel(empty),
      empty
    )
    return
  }

  // A restart sends RESET before it bumps the generation, so a session that
  // is no longer ready counts as reset too.
  const generation = state.session.generation
  const isCurrent = (): boolean => {
    const { session } = store.getState()
    return session.generation === generation && session.status === "ready"
  }

  if (plan.panels) {
    if (plan.panels.length > 0) {
      await runPanels(
        store,
        queue,
        tileId,
        plan.panels,
        plan.outputIndex,
        isCurrent
      )
      return
    }
    const variable = variableLabel(repeatOf(tile)?.variable ?? "")
    const none = `${variable} has no categories to repeat by.`
    setTileQueued(tileId, "", true)
    applyTileResult(
      tileId,
      "",
      { label: tileId, content: { data: [] } },
      messageTableModel(none),
      none
    )
    return
  }

  setTileQueued(tileId, plan.script, plan.usesUniverse)
  setTileRunning(tileId)
  const declared = plan.universe && plan.emitPrefix ? plan.universe : null
  if (declared) markKnownFilterField(declared.fieldName)

  try {
    const result = await runWithRetry(queue, plan.script, tileId, isCurrent)
    if (plan.quietScript) queue.seed(plan.quietScript, result)
    const output = result.result?.output
    const item = output?.[plan.outputIndex] ?? output?.[0]
    if (!item) {
      setTileError(tileId, plan.script, NO_OUTPUT)
      return
    }
    const { model, message } = toTileResult(item)
    applyTileResult(tileId, plan.script, item, model, message)
  } catch (e) {
    // Not sent because the session moved on: a restart stales every tile, so
    // this one re-runs on the current queue, or the dashboard is gone.
    if (e instanceof SupersededRunError) return
    cleanUpFailedRun(store, queue, tileId, plan.script, declared, isCurrent)
    setTileError(tileId, plan.script, errorText(e))
  }
}
